// Shared institutional constants (LSC campuses + student academic-plan options).
// Values match the backend (server.py) and the CSV import template.

export const CAMPUSES = [
  "CyFair", "Houston North", "Kingwood", "Montgomery",
  "North Harris", "Tomball", "University Park", "Online",
];

export const ENROLLMENT_GOALS = [
  { v: "degree", l: "Earning a degree / certificate at LSC" },
  { v: "transfer", l: "Transferring to a 4-year university" },
  { v: "non_degree", l: "Non-degree / continuing education" },
  { v: "workforce", l: "Workforce / career skills" },
  { v: "exploring", l: "Exploring / undecided" },
];

export const ENROLLMENT_INTENSITY = [
  { v: "full_time", l: "Full-time" },
  { v: "part_time", l: "Part-time" },
];

export const SUPPORT_INTERESTS = [
  { v: "internships", l: "Internships" },
  { v: "job_placement", l: "Job placement" },
  { v: "scholarships", l: "Scholarships / financial aid" },
  { v: "tutoring", l: "Tutoring" },
  { v: "transfer_advising", l: "Transfer advising" },
  { v: "career_coaching", l: "Career coaching" },
];

export const GOAL_LABEL = Object.fromEntries(ENROLLMENT_GOALS.map((o) => [o.v, o.l]));
export const INTENSITY_LABEL = Object.fromEntries(ENROLLMENT_INTENSITY.map((o) => [o.v, o.l]));
export const SUPPORT_LABEL = Object.fromEntries(SUPPORT_INTERESTS.map((o) => [o.v, o.l]));

// Lone Star College programs of study (from lonestar.edu/programs-of-study/all-programs.htm),
// organized by LSC Area of Study for scannability. Used by the Program dropdown.
export const LSC_PROGRAM_GROUPS = [
  {
    area: "University Transfer (General)",
    programs: [
      "Associate of Arts",
      "Associate of Arts in Teaching",
      "Associate of Science",
    ],
  },
  {
    area: "Arts, Humanities, Communication & Design",
    programs: [
      "Architectural Design Certificate Level I",
      "Architectural Design Technology AAS Degree",
      "Game Design & Simulation Designer (Artist) Specialization AAS Degree",
      "Music AA Degree",
      "Speech Communication AA Degree",
    ],
  },
  {
    area: "Business & Professional Services",
    programs: [
      "Accounting AAS Degree",
      "Accounting Advanced Technical Certificate in Professional Accounting",
      "Accounting Certificate Level I",
      "Administrative Services AAS Degree: Medical",
      "Business AA Degree",
      "Business Administration AAS Degree: Business Management Specialization",
      "Business Administration AAS Degree: Human Resource Specialization",
      "Business Administration AAS Degree: Marketing Specialization",
      "Business Administration Certificate Level II: Business Management Specialization",
      "Business Administration Certificate Level II: Human Resource Specialization",
      "Business Administration Certificate Level II: Marketing Specialization",
      "Esports Management Certificate Level II",
      "Leadership Skills for The New Supervisor FastTrack NON-CREDIT",
      "Leadership Skills for The Experienced Supervisor FastTrack NON-CREDIT",
      "Lean Six Sigma Green Belt Certificate FastTrack NON-CREDIT",
      "Management Certificate FastTrack NON-CREDIT",
      "Payroll Assistant Certificate FastTrack NON-CREDIT",
      "Project Management Certification FastTrack NON-CREDIT",
      "SAP - Enterprise Systems Business Analyst",
    ],
  },
  {
    area: "Information Technology & Computer Science",
    programs: [
      "Bachelor of Applied Technology in Cybersecurity",
      "Computer Programming Specialist AAS Degree",
      "Computer Science AS Degree",
      "Cybersecurity AAS Degree",
      "Game Design & Simulation Developer (Programming) Specialization AAS Degree",
      "Geographic Information Systems AAS Degree",
      "Geographic Information Systems Technician Certificate Level I",
    ],
  },
  {
    area: "Engineering, Mathematics & Sciences",
    programs: [
      "Biology",
      "Biomedical Engineering",
      "Biotechnology AAS Degree",
      "Biotechnology Laboratory Technician Advanced Technical Certificate",
      "Civil Engineering",
      "Electrical Engineering",
      "Industrial Engineering",
      "Mechanical Design Certificate Level I",
      "Mechanical Design Technology AAS Degree",
      "Mechanical Engineering",
    ],
  },
  {
    area: "Health Sciences",
    programs: [
      "Bachelor of Science, Nursing (RN to BSN transition program)",
      "Dental Assistant Certificate FastTrack NON-CREDIT",
      "Dental Hygiene AAS Degree",
      "Diagnostic Medical Sonography AAS Degree",
      "Echocardiography AAS Degree",
      "Electrocardiography Monitor Technician Certificate FastTrack NON-CREDIT",
      "Emergency Medical Services Professions AAS Degree",
      "Emergency Medical Technician (EMT) Certificate",
      "Emergency Medical Technician (EMT) Certificate FastTrack NON-CREDIT",
      "Health Information Technology AAS Degree",
      "Medical Coder Certificate",
      "Nurse Aide (CNA) Certificate",
      "Nursing Assistant Certificate Level I (Integrated Curriculum)",
      "Nursing Specialization AAS Degree (Integrated Curriculum)",
      "Nursing Specialization AAS Degree (Block Curriculum)",
      "Occupational Therapy Assistant AAS Degree",
      "Occupational Therapy Practitioner Re-entry",
      "Patient Care Technician Certificate",
      "Phlebotomist",
      "Transition To Professional Nursing For Articulating Students AAS Degree (Integrated Curriculum)",
      "Vocational Nursing Certificate Level II",
      "Vocational Nursing ADN To VN Certificate Level II",
    ],
  },
  {
    area: "Energy, Manufacturing & Construction",
    programs: [
      "AutoCAD Draftsman Certificate FastTrack NON-CREDIT",
      "AWS Welder Master",
      "Bachelor of Applied Science in Energy, Manufacturing, & Trades Management",
      "Construction Supervision AAS Degree",
      "Construction Supervision Certificate Level II",
      "Electrical Technology AAS Degree",
      "Field Service Technician Certificate Level I",
      "HVAC & Refrigeration AAS Degree",
      "HVAC & Refrigeration Residential Servicing Certificate Level I",
      "HVAC Occupational Entry Certificate",
      "Industrial Diesel Technology AAS Degree",
      "Instrumentation Technology AAS Degree",
      "Mechatronics Technician AAS Degree",
      "Petroleum Service Technology AAS Degree",
      "Pre-Apprentice Mechatronics FastTrack NON-CREDIT",
      "Process Technology AAS Degree",
      "SolidWorks Designer Certificate FastTrack NON-CREDIT",
    ],
  },
  {
    area: "Transportation & Logistics",
    programs: [
      "Automotive Service Technician AAS Degree",
      "Automotive Service Technician Certificate Level I",
      "Automotive Service Technician Certificate Level II",
      "Automotive Technician Certificate FastTrack NON-CREDIT",
    ],
  },
  {
    area: "Public Safety & Human/Consumer Services",
    programs: [
      "Cosmetology AAS Degree",
      "Cosmetology Facial AAS Degree",
      "Cosmetology Instructor AAS Degree",
      "Facial Specialist Certificate Level I",
    ],
  },
  {
    area: "Social/Behavioral Sciences & Education",
    programs: [
      "Criminal Justice AA Degree",
    ],
  },
];

// Flat list (single source of truth for membership checks / validation).
export const LSC_PROGRAMS = LSC_PROGRAM_GROUPS.flatMap((g) => g.programs);
